import {
    selectDesignatedCompanionDevice,
    selectHandoverToDesktop,
} from 'flow/selectors/app-selectors';
import { State } from 'flow/types/State';
import { isEmbeddedTypeCti, isTeamsAnyVersion } from 'flow/utils/app-utils';
import {
    geoRestrictionIsDeployed,
    geoRestrictionIsEnabled,
} from 'flow/utils/geolocation-utils';
import {
    CompanionLocationStatus,
    isCompanionModeEnabled,
    isStaleCompanionDevice,
} from 'flow/companion/companion-mode';

export const CompanionRequirement = Object.freeze({
    MET: 'MET',
    PENDING: 'PENDING',
    NOT_ENABLED: 'NOT_ENABLED',
    HANDOVER_OFF: 'HANDOVER_OFF',
    NO_COMPANION: 'NO_COMPANION',
    LOCATION_BLOCKED: 'LOCATION_BLOCKED',
    OUTSIDE_REGION: 'OUTSIDE_REGION',
} as const);

export type CompanionRequirementType =
    (typeof CompanionRequirement)[keyof typeof CompanionRequirement];

export const selectCompanionIsRequired = (state: State) => {
    const embedded = state.app.get('embedded');

    return (
        geoRestrictionIsDeployed() &&
        geoRestrictionIsEnabled(state.app.get('geoRestrictionEnabled')) &&
        !!embedded &&
        (isEmbeddedTypeCti(embedded) || isTeamsAnyVersion(embedded))
    );
};

export const selectCompanionLocationStatus = (state: State): string | null =>
    state.app.get('companionLocationStatus') ?? null;

export const selectCompanionKnownLocationStatus = (
    state: State,
): string | null => state.app.get('companionKnownLocationStatus') ?? null;

export const selectCompanionReportedLocationStatus = (
    state: State,
): string | null => state.app.get('companionReportedLocationStatus') ?? null;

export const selectCompanionLocationReportedAt = (
    state: State,
): number | null => state.app.get('companionLocationReportedAt') ?? null;

export const selectCompanionHeartbeatEnabled = (state: State) =>
    state.app.get('companionHeartbeatEnabled') === true;

export const selectCompanionResponsive = (state: State): boolean | null =>
    state.app.get('companionResponsive') ?? null;

export const selectCompanionRequirement = (
    state: State,
): CompanionRequirementType => {
    if (!selectCompanionIsRequired(state)) {
        return CompanionRequirement.MET;
    }

    if (!isCompanionModeEnabled(state)) {
        return state.app.get('companionEnabled') === undefined
            ? CompanionRequirement.PENDING
            : CompanionRequirement.NOT_ENABLED;
    }

    if (!selectHandoverToDesktop(state)) {
        return CompanionRequirement.HANDOVER_OFF;
    }

    if (state.app.get('companionDevices') === undefined) {
        return CompanionRequirement.PENDING;
    }

    const designated = selectDesignatedCompanionDevice(state);

    if (!designated || isStaleCompanionDevice(designated)) {
        return CompanionRequirement.NO_COMPANION;
    }

    const locationStatus = selectCompanionLocationStatus(state);

    if (locationStatus === CompanionLocationStatus.OUTSIDE_REGION) {
        return CompanionRequirement.OUTSIDE_REGION;
    }

    if (locationStatus && locationStatus !== CompanionLocationStatus.OK) {
        return CompanionRequirement.LOCATION_BLOCKED;
    }

    return CompanionRequirement.MET;
};

export const selectCallingNeedsCompanion = (state: State) =>
    selectCompanionRequirement(state) !== CompanionRequirement.MET;
